import { api } from './api';

export interface PoolContact {
  id: number;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  email: string | null;
  company: string | null;
  position: string | null;
  source: string;
  source_url: string | null;
  phone: string | null;
  website: string | null;
  category: string | null;
  ai_confidence: number | null;
  ai_reasoning: string | null;
  ai_niche: string | null;
  status: string;
  reviewed_at: string | null;
  imported_at: string;
  import_batch_id: string | null;
  notes: string | null;
  client_id: number | null;
  client_name: string | null;
  promotion_count?: number;
  promotions: Array<{ target_type: string; target_id: number }>;
}

export interface PoolStats {
  total: number;
  pending: number;
  by_category: Record<string, number>;
}

export interface PoolFilters {
  status: string | null;
  category: string | null;
  source: string | null;
  client_id: number | null;
  website: string | null;   // contacts found for a specific lead (contact_pool.website = lead domain)
  search: string;
  sort_by: string;
  sort_dir: string;
  limit: number;
  offset: number;
}

export const contactPoolApi = {
  getStats: () => api.get<PoolStats>('/contact-pool/stats'),

  listContacts: (f: Partial<PoolFilters>) => {
    const qs = new URLSearchParams();
    if (f.status) qs.set('status', f.status);
    if (f.category) qs.set('category', f.category);
    if (f.source) qs.set('source', f.source);
    if (f.client_id != null) qs.set('client_id', String(f.client_id));
    if (f.website) qs.set('website', f.website);
    if (f.search) qs.set('search', f.search);
    if (f.sort_by) qs.set('sort_by', f.sort_by);
    if (f.sort_dir) qs.set('sort_dir', f.sort_dir);
    if (f.limit != null) qs.set('limit', String(f.limit));
    if (f.offset != null) qs.set('offset', String(f.offset));
    const q = qs.toString();
    return api.get<{ items: PoolContact[]; total: number }>(`/contact-pool/contacts${q ? `?${q}` : ''}`);
  },

  getContact: (id: number) => api.get<PoolContact>(`/contact-pool/contacts/${id}`),

  createContact: (data: Partial<PoolContact>) =>
    api.post<PoolContact>('/contact-pool/contacts', data),

  updateContact: (id: number, data: Partial<PoolContact>) =>
    api.patch<PoolContact>(`/contact-pool/contacts/${id}`, data),

  review: (id: number, action: 'accept' | 'reject') =>
    api.post<PoolContact>(`/contact-pool/contacts/${id}/review`, { action }),

  bulkReview: (ids: number[], action: 'accept' | 'reject') =>
    api.post<{ updated: number }>('/contact-pool/contacts/bulk-review', { ids, action }),

  promote: (id: number, target_type: string, user_id?: number, client_id?: number | null) =>
    api.post<{ contact_id: number; target_type: string; target_id: number }>(
      `/contact-pool/contacts/${id}/promote`,
      {
        target_type,
        ...(user_id != null ? { user_id } : {}),
        ...(client_id != null ? { client_id } : {}),
      },
    ),

  unpromote: (id: number) =>
    api.delete<PoolContact>(`/contact-pool/contacts/${id}/promote`),

  bulkAssignClient: (ids: number[], client_id: number | null) =>
    api.post<{ updated: number }>('/contact-pool/contacts/bulk-assign-client', { ids, client_id }),

  reclassify: (id: number) =>
    api.post<{ status: string }>(`/contact-pool/contacts/${id}/reclassify`, {}),

  bulkReclassify: (ids: number[]) =>
    api.post<{ status: string; count: number }>('/contact-pool/contacts/bulk-reclassify', { ids }),
};
